import React, { useMemo, useState } from "react";
import { formatMoneyByCurrency } from "../utils/currency";
import { absMediaUrl } from "../utils/mediaUrl";
import { TrendingRangeKey as RangeKey, rangeCutoff } from "../utils/adminOrderStats";
import type { TrendingSort as SongSort, TrendingSort as ArtistSort } from "../types/admin";
import StatsPending from "./admin-stats/StatsPending";
import { statsUrl, trendingFrom } from "./admin-stats/api";
import { useAdminRead } from "../hooks/useAdminRead";

// Trending tab — the most requested songs and artists in a rolling window,
// counted on the server over the whole order history (GET
// /admin/stats/trending, server lib/admin/stats.js). The answer carries the
// top 20 for every sort and direction, so a header click is instant.

const RANGE_ORDER: RangeKey[] = ["1W", "1M", "3M", "1Y", "all"];
const RANGE_LABELS: Record<RangeKey, string> = {
  "1W": "Last week",
  "1M": "Last month",
  "3M": "Last 3 months",
  "1Y": "Last year",
  all: "All Time",
};

export default function AdminTrendingTab() {
  const [songSort, setSongSort] = useState<SongSort>("total");
  const [songDir, setSongDir] = useState<"desc" | "asc">("desc");
  const [artistSort, setArtistSort] = useState<ArtistSort>("total");
  const [artistDir, setArtistDir] = useState<"desc" | "asc">("desc");
  const [range, setRange] = useState<RangeKey>("all");

  // Rolling cutoff: createdAt >= now - range (calendar months / years, as
  // before); the server answers every sort for that window.
  const since = useMemo(() => rangeCutoff(range), [range]);
  const { data, failed, retry } = useAdminRead(statsUrl("trending", since), trendingFrom);
  const trendingSongs = data ? data.songRanks[songSort][songDir].map((i) => data.songs[i]) : [];
  const topArtists = data ? data.artistRanks[artistSort][artistDir].map((i) => data.artists[i]) : [];

  const toggleSongSort = (field: SongSort) => {
    if (songSort === field) setSongDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setSongSort(field); setSongDir("desc"); }
  };
  const toggleArtistSort = (field: ArtistSort) => {
    if (artistSort === field) setArtistDir((d) => (d === "desc" ? "asc" : "desc"));
    else { setArtistSort(field); setArtistDir("desc"); }
  };

  const sortArrow = (active: boolean, dir: "desc" | "asc") => {
    if (!active) return <span className="muted-text adm-ml-2">↕</span>;
    return <span className="adm-ml-2">{dir === "desc" ? "↓" : "↑"}</span>;
  };

  const periodLabel = RANGE_LABELS[range];

  return (
    <div className="adm-tab-container fade-in">
      {/* Rolling time filter */}
      <div className="adm-toolbar adm-mb-14">
        <div className="adm-toolbar-group">
          {RANGE_ORDER.map((r) => (
            <button
              key={r}
              className={`filter-chip ${range === r ? "active" : ""}`}
              onClick={() => setRange(r)}
            >
              {r === "all" ? "All" : r}
            </button>
          ))}
        </div>
        <span className="muted-text adm-fs-76">
          {data ? data.orderCount : "…"} orders · {periodLabel}
        </span>
      </div>

      {!data ? (
        <StatsPending failed={failed} onRetry={retry} />
      ) : (
      <>
      {/* Trending Songs */}
      <div className="adm-card adm-mb-16">
        <h2 className="adm-section-title adm-mb-12">Trending Songs</h2>
        <div className="adm-scrollable-full" style={{ maxHeight: 520 }}>
          <table className="adm-table-light adm-table-compact sticky-header adm-w-full">
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "28%" }}>Track</th>
                <th style={{ width: "18%" }}>Artist</th>
                <th className="adm-sortable" style={{ width: "10%" }} onClick={() => toggleSongSort("paid")}>
                  Paid {sortArrow(songSort === "paid", songDir)}
                </th>
                <th className="adm-sortable" style={{ width: "10%" }} onClick={() => toggleSongSort("selected")}>
                  Selected {sortArrow(songSort === "selected", songDir)}
                </th>
                <th className="adm-sortable" style={{ width: "10%" }} onClick={() => toggleSongSort("total")}>
                  Total {sortArrow(songSort === "total", songDir)}
                </th>
                <th className="adm-sortable" style={{ width: "12%" }} onClick={() => toggleSongSort("revenue")}>
                  Revenue {sortArrow(songSort === "revenue", songDir)}
                </th>
              </tr>
            </thead>
            <tbody>
              {trendingSongs.map((s, i) => {
                const img = absMediaUrl(s.image ?? undefined);
                const total = s.paidCount + s.selectedCount;
                return (
                  <tr key={`song-${i}`} className="hover-row">
                    <td className="muted-text adm-semibold">{i + 1}</td>
                    <td>
                      <div className="dj-cell dj-cell-xs">
                        {img ? (
                          <img src={img} className="track-img-xs" alt="" />
                        ) : (
                          <div className="track-placeholder-xs">♪</div>
                        )}
                        <span className="truncate-inline adm-semibold adm-fs-82">
                          {s.query}
                        </span>
                      </div>
                    </td>
                    <td className="muted-text adm-fs-78">
                      {s.artists.join(", ") || "—"}
                    </td>
                    <td className="adm-bold adm-green">{s.paidCount}</td>
                    <td className="adm-semibold adm-orange">{s.selectedCount > 0 ? s.selectedCount : "—"}</td>
                    <td className="adm-bold">{total}</td>
                    <td className="adm-semibold adm-green">{formatMoneyByCurrency(s.revenueByCurrency)}</td>
                  </tr>
                );
              })}
              {trendingSongs.length === 0 && (
                <tr>
                  <td colSpan={7} className="adm-center adm-p-24 adm-grey">
                    No songs with 3+ requests {range !== "all" ? "in this period" : "yet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Artists */}
      <div className="adm-card">
        <h2 className="adm-section-title adm-mb-12">Top Artists</h2>
        <div className="adm-scrollable-full" style={{ maxHeight: 480 }}>
          <table className="adm-table-light adm-table-compact sticky-header adm-w-full">
            <thead>
              <tr>
                <th style={{ width: "5%" }}>#</th>
                <th style={{ width: "30%" }}>Artist</th>
                <th className="adm-sortable" style={{ width: "12%" }} onClick={() => toggleArtistSort("paid")}>
                  Paid {sortArrow(artistSort === "paid", artistDir)}
                </th>
                <th className="adm-sortable" style={{ width: "12%" }} onClick={() => toggleArtistSort("selected")}>
                  Selected {sortArrow(artistSort === "selected", artistDir)}
                </th>
                <th className="adm-sortable" style={{ width: "12%" }} onClick={() => toggleArtistSort("total")}>
                  Total {sortArrow(artistSort === "total", artistDir)}
                </th>
                <th className="adm-sortable" style={{ width: "15%" }} onClick={() => toggleArtistSort("revenue")}>
                  Revenue {sortArrow(artistSort === "revenue", artistDir)}
                </th>
              </tr>
            </thead>
            <tbody>
              {topArtists.map((a, i) => {
                const total = a.paidCount + a.selectedCount;
                return (
                  <tr key={`artist-${i}`} className="hover-row">
                    <td className="muted-text adm-semibold">{i + 1}</td>
                    <td className="adm-semibold adm-fs-82">{a.name}</td>
                    <td className="adm-bold adm-green">{a.paidCount}</td>
                    <td className="adm-semibold adm-orange">{a.selectedCount > 0 ? a.selectedCount : "—"}</td>
                    <td className="adm-bold">{total}</td>
                    <td className="adm-semibold adm-green">{formatMoneyByCurrency(a.revenueByCurrency)}</td>
                  </tr>
                );
              })}
              {topArtists.length === 0 && (
                <tr>
                  <td colSpan={6} className="adm-center adm-p-24 adm-grey">
                    No artist data {range !== "all" ? "in this period" : "yet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
